import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { parseISO, format } from 'date-fns';

import moment from 'moment';

import Loading from "../../components/Loading";
import Data from '../../../repository';

import styles from '../../../styles/Admin.module.scss';

function List() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(true);
	const [bookings, setBookings] = useState([]);
	const [filters, setFilters] = useState({
		status: '',
		staff: '',
		start: moment().format('YYYY-MM-DD'),
		end: moment().add(30, 'days').format('YYYY-MM-DD')
	});

	useEffect(() => {
		setSearchParams({
			start: filters.start,
			end: filters.end
		});
	}, []);

	useEffect(() => {
		fetchBookings();
	}, [searchParams]);

	const fetchBookings = async () => {
		setLoading(true);
		try {
			const res = await Data.fetchServiceBookings(filters.start, filters.end);
			setBookings(res.data);
		} catch (error) {
			console.error('Error fetching bookings:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleFilterChange = (key, value) => {
		const newFilters = { ...filters, [key]: value };
		setFilters(newFilters);
		if (key === 'start' || key === 'end') {
			setSearchParams({ start: newFilters.start, end: newFilters.end });
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case 'confirmed': return '#28a745';
			case 'pending': return '#ffc107';
			case 'cancelled': return '#dc3545';
			case 'completed': return '#17a2b8';
			case 'no_show': return '#6c757d';
			default: return '#666';
		}
	};

	if (loading) return <Loading button={false} />;

	// Apply filters
	let output = bookings;
	if (filters.status) {
		output = output.filter(b => b.status === filters.status);
	}
	if (filters.staff) {
		output = output.filter(b => b.Staff?._id === filters.staff);
	}

	// Get unique staff for filter
	const staffList = [...new Map(bookings.map(b => [b.Staff?._id, b.Staff]).filter(([id]) => id)).values()];

	return (
		<div className={styles.withFilters}>
			<div className={styles.filters}>
				<div>
					<label>Start Date</label>
					<DatePicker
						value={filters.start ? parseISO(filters.start) : null}
						onChange={(date) => handleFilterChange('start', date ? format(date, 'yyyy-MM-dd') : '')}
					/>
				</div>
				<div>
					<label>End Date</label>
					<DatePicker
						value={filters.end ? parseISO(filters.end) : null}
						onChange={(date) => handleFilterChange('end', date ? format(date, 'yyyy-MM-dd') : '')}
					/>
				</div>
				<div>
					<label>Status</label>
					<select
						value={filters.status}
						onChange={(e) => handleFilterChange('status', e.target.value)}
					>
						<option value="">All Statuses</option>
						<option value="pending">Pending</option>
						<option value="confirmed">Confirmed</option>
						<option value="completed">Completed</option>
						<option value="cancelled">Cancelled</option>
						<option value="no_show">No Show</option>
					</select>
				</div>
				<div>
					<label>Staff</label>
					<select
						value={filters.staff}
						onChange={(e) => handleFilterChange('staff', e.target.value)}
					>
						<option value="">All Staff</option>
						{staffList.map(staff => (
							<option key={staff._id} value={staff._id}>
								{staff.firstName} {staff.lastName}
							</option>
						))}
					</select>
				</div>
			</div>

			<div className={styles.list}>
				{!output.length && <div><p>There aren't any service bookings to show.</p></div>}

				{output.map((booking) => (
					<Link
						key={booking._id}
						to={booking._id}
						style={{ textDecoration: 'none', color: 'inherit' }}
					>
						<div className={styles.service} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<div>
								<strong>{booking.Service?.name}</strong>
								<p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
									{booking.User?.firstName} {booking.User?.lastName} - with {booking.Staff?.firstName} {booking.Staff?.lastName}
								</p>
								<p style={{ margin: '4px 0 0 0', color: '#888', fontSize: '13px' }}>
									{moment(booking.date).format('ddd, MMM D, YYYY')} at {booking.startTime}
								</p>
							</div>
							<div style={{ textAlign: 'right' }}>
								<span
									style={{
										background: getStatusColor(booking.status),
										color: '#fff',
										padding: '4px 12px',
										borderRadius: '4px',
										fontSize: '12px',
										textTransform: 'uppercase'
									}}
								>
									{booking.status}
								</span>
								{booking.paymentVerificationStatus === 'mismatch' && (
									<span
										title={booking.paymentVerificationReason || 'The payment provider has no matching successful payment on record.'}
										style={{
											marginLeft: '6px',
											background: '#dc3545',
											color: '#fff',
											padding: '4px 12px',
											borderRadius: '4px',
											fontSize: '12px',
											textTransform: 'uppercase'
										}}
									>
										Payment not confirmed
									</span>
								)}
								<p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '13px' }}>
									Ref: {booking.reference}
								</p>
							</div>
						</div>
					</Link>
				))}
			</div>
		</div>
	);
}

export default List;
